import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { AppBar, CircularProgress, createStyles, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Toolbar, Typography, withStyles, WithStyles } from '@material-ui/core';
import { SystemsData } from './SystemsContext';
import { splitHostString } from './ActivateLicenseData';
import { Utilities } from '../general/utilities';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      alignItems: "left",
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    button: {
      margin: '0px 20px 0px 0px',
    },
    tableContainer: {
      borderRadius: 0,
      maxHeight: 'calc(100vh - 500px)'
    },
    historyRow: {
      background: DeltaXMLColors.OXFORD_BLUE,
    },
    historyRowCellText: {
      color: MyDeltaColors.WHITE
    },
    progress: {
      float: 'right',
      margin: '2px 8px 0px 0px',
      color: MyDeltaColors.WHITE
    },
    buttonRefresh: {
      float: 'right',
    }
  });

export interface RawActivationData  {
  "audit_ip": string,
  "audit_time": string,
  "audit_user": string,
  "hostid": string,
  "hostid2": string,
  "hostid3": string,
  "operation": "ACTIVATE" | "DEACTIVATE" | "REGENERATE",
  "port": number
}

export interface HistoryDialogProps extends WithStyles<typeof styles> {
  dialogOpen: boolean;
  handleHistoryClose: () => void;
  handleRefresh: () => void;
  systemData: SystemsData;
  fetchingHistory: boolean;
  noHostIdsRequired: boolean;
  dateFormat: string;
  timeFormat: string;
}

function HistoryDialog(props: HistoryDialogProps) {
  const { classes, dialogOpen, handleHistoryClose, handleRefresh, systemData, fetchingHistory, noHostIdsRequired, dateFormat, timeFormat } = props;
  const { rawActivations } = systemData;
  let returnCountSumFromHistory = 0;
  let isLatestHistoryItem = true;

  const checkedRawActivations = rawActivations? rawActivations : [];
  const sortedRawActivations = checkedRawActivations.sort(Utilities.compareRawActivation);
  let activationsUpToDate = false;
  let latestHistoryItem: RawActivationData | undefined;

  const rowElements: JSX.Element[] = sortedRawActivations.map((item) => {
    let opDescription = item.operation === 'ACTIVATE' ? 'Activate' : item.operation === 'DEACTIVATE' ? 'Return' : item.operation === 'REGENERATE' ? 'Download' : item.operation;
    if (isLatestHistoryItem) {
      latestHistoryItem = item;
      isLatestHistoryItem = false;
    }
    if (item.operation === 'DEACTIVATE') {
      returnCountSumFromHistory++;
    }

    return (
    <TableRow key={item.audit_time}>
      <TableCell>{opDescription}</TableCell>
      {(item.hostid2 === "" && item.hostid3 === "") &&
        <TableCell>{item.hostid}</TableCell>
      }
      {(item.hostid2 !== "" && item.hostid3 !== "") &&
        <TableCell>{item.hostid}<br/>{item.hostid2}<br/>{item.hostid3}</TableCell>
      }
      <TableCell>{DTFormatter.getFormattedDateTime(item.audit_time, dateFormat, timeFormat)}</TableCell>
      <TableCell>{item.audit_user}</TableCell>
      <TableCell>{item.audit_ip}</TableCell>
    </TableRow>
    )
  });

  const returnCountIsUpToDate = returnCountSumFromHistory === systemData.returnCount;

  // if (systemData.hostId === undefined || systemData.hostId === "" || latestHostIdsFromHistory === null || latestHostIdsFromHistory === "") {
  //   activationsUpToDate = (systemData.hostId === undefined || systemData.hostId === "") && (latestHostIdsFromHistory === null || latestHostIdsFromHistory === "");
  // } else {
  //   const { hostType, hostId } = splitHostString(latestHostIdsFromHistory);
  //   activationsUpToDate = hostType === systemData.hostIdType && hostId === systemData.hostId;
  // }
  const item = latestHistoryItem;
  if (item) {
    const { hostType: hostType1, hostId: hostId1} = splitHostString(item.hostid);
    const { hostType: hostType2, hostId: hostId2} = splitHostString(item.hostid2);
    const { hostType: hostType3, hostId: hostId3} = splitHostString(item.hostid3);
    if (item.operation === 'ACTIVATE') {
      const noHostId = (
        (systemData.hostId === undefined || systemData.hostId === "") &&
        (item.hostid === undefined || item.hostid === "")
      );
      const isThreeServerRedundancyEnabled = hostType2 && hostId2 && hostType3 && hostId3;
      activationsUpToDate = (noHostId ||
        (
          isThreeServerRedundancyEnabled ? 
          (
            hostId1 === systemData.hostId &&
            hostId2 === systemData.hostId2 &&
            hostId3 === systemData.hostId3 &&
            hostType1 === systemData.hostIdType &&
            hostType2 === systemData.hostId2Type &&
            hostType3 === systemData.hostId3Type
          ) :
          (
            hostId1 === systemData.hostId &&
            hostType1 === systemData.hostIdType 
          )
        )
        
      )
    } else if (item.operation === 'DEACTIVATE') {
      // we should only need to check the first hostid because
      // if the firstId is empty then hostId2 and hostId2 SHOULD also be empty
      activationsUpToDate = (systemData.hostId === undefined || systemData.hostId === "") &&
        (item.hostid === undefined || item.hostid === "");
    } else if (item.operation === 'REGENERATE') {
      // in this case, we cant check that the history is current because
      // the hostid, hostid2 and hostid3 values are all empty-strings
      activationsUpToDate = noHostIdsRequired || (systemData.hostId !== undefined && systemData.hostId !== "");
    }

  } else {
    // no history items - if hostId has been set locally then there SHOULD be a history item
    activationsUpToDate = (systemData.hostId === undefined || systemData.hostId === "");
  }

  const activationsAndReturnCountsUpToDate = activationsUpToDate && returnCountIsUpToDate;
  const historyState = fetchingHistory? "Fetching..." : activationsAndReturnCountsUpToDate? "Current" : "Stale";

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleHistoryClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth={true}
      maxWidth="lg"
    >
      <AppBar className={classes.appBar} position="static">
        <Toolbar>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={2}>
              <Typography variant="h4">License History</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">History State: <span style={{ color: activationsAndReturnCountsUpToDate? MyDeltaColors.SILVER : 'gold' }}>{historyState}</span></Typography>
            </Grid>
            <Grid item xs={2}>
              {(!fetchingHistory && !noHostIdsRequired) &&
                <Typography variant="body2">Return Count: <span style={{ color: MyDeltaColors.SILVER }}>{systemData.returnCount}</span></Typography>
              }
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2">System ID: <span style={{ color: MyDeltaColors.SILVER }}>{systemData.systemId}</span></Typography>
            </Grid>
            <Grid item xs={1}>
              {fetchingHistory &&
                <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
              }
            </Grid>
            <Grid item xs={1}>
              <Button variant="contained" color="secondary" className={classes.buttonRefresh} onClick={handleRefresh}>Refresh</Button>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <br />
      <DialogContent>
        <TableContainer className={classes.tableContainer} component={Paper}>
          <Table>
            <TableHead>
              <TableRow className={classes.historyRow}>
                <TableCell className={classes.historyRowCellText}>Operation</TableCell>
                <TableCell className={classes.historyRowCellText}>Host</TableCell>
                <TableCell className={classes.historyRowCellText}>Time</TableCell>
                <TableCell className={classes.historyRowCellText}>User</TableCell>
                <TableCell className={classes.historyRowCellText}>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowElements}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button className={classes.button} variant="contained" color="primary" onClick={handleHistoryClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
export default withStyles(styles)(HistoryDialog);